/**
 * Returns the length of a vector
 *
 * @param {Vector}
 * @return {Float}
 */
export function vectorLength(v) {
  const { x, y } = v;
  return Math.sqrt(x ** 2 + y ** 2);
}


/**
 * Calculates the angle between a line a the yAxis
 *
 * @param {Array}
 * @return {Float}
 */
export function getAngle(line) {
  const deltaY = line[1].y - line[0].y;
  const deltaX = line[1].x - line[0].x;

  // Ensure non-zero denominator to avoid division by zero
  const angle = deltaX !== 0 ? Math.atan(deltaY / deltaX) : Math.PI / 2;

  return angle;
}


/**
 * Rotates a vector by a given angle
 *
 * @param {Vector}
 * @param {Float} Angle in radians
 * @return {Vector}
 */
export function rotateVector(vector, angle) {
  if (!angle) {
    return vector;
  }

  const { x, y } = vector;
  const rotatedX = Math.cos(angle) * x - Math.sin(angle) * y;
  const rotatedY = Math.sin(angle) * x + Math.cos(angle) * y;

  return { x: rotatedX, y: rotatedY };
}


/**
 * Solves a 2D equation system
 * a + r*b = c, where a,b,c are 2D vectors
 *
 * @param {Vector}
 * @param {Vector}
 * @param {Vector}
 * @return {Float}
 */
function solveLambdaSystem(a, b, c) {
  const system = [
    { n: a[0] - c[0], lambda: b[0] },
    { n: a[1] - c[1], lambda: b[1] }
  ];

  const n = system.reduce((sum, term) => sum + term.n * term.lambda, 0);
  const l = system.reduce((sum, term) => sum + term.lambda * term.lambda, 0);

  return l !== 0 ? -n / l : undefined;
}


/**
 * Position of perpendicular foot
 *
 * @param {Point}
 * @param [ {Point}, {Point} ] line defined through two points
 * @return {Point} the perpendicular foot position
 */
export function perpendicularFoot(point, line) {
  const [a, b] = line;

  // relative position of b from a
  const bd = { x: b.x - a.x, y: b.y - a.y };

  // solve equation system to the parametrized vectors param real value
  const r = solveLambdaSystem([a.x, a.y], [bd.x, bd.y], [point.x, point.y]);

  const result = {
    x: a.x + r * bd.x,
    y: a.y + r * bd.y,
  };

  return result;
}


/**
 * Calculates the distance between a point and a line
 *
 * @param {Point}
 * @param [ {Point}, {Point} ] line defined through two points
 * @return {Float} distance
 */
export function getDistancePointLine(point, line) {
  const pfPoint = perpendicularFoot(point, line);

  // distance vector
  const connectionVector = {
    x: pfPoint.x - point.x,
    y: pfPoint.y - point.y
  };

  return vectorLength(connectionVector);
}


/**
 * Calculates the distance between two points
 *
 * @param {Point}
 * @param {Point}
 * @return {Float} distance
 */
export function getDistancePointPoint(point1, point2) {
  const vector = {
    x: point1.x - point2.x,
    y: point1.y - point2.y
  };

  return vectorLength(vector);
}